import { z } from 'zod';

import { TRANSLATION_LOCALES } from '../../common/translations/translations';
import { NotificationChannelSchema } from '../notification-channel/notification-channel';
import { NotificationTemplateKeySchema } from '../notification-template-key/notification-template-key';
import {
  NotificationTemplateTranslationsSchema,
  bodyMaxLengthFor,
} from '../notification-template/notification-template';

/**
 * The editable half of a template.
 *
 * Declared as a raw shape so the update schema can `.partial()` it — the same
 * reason `ViolationTypeWritableShape` exists.
 *
 * **`category` is not here.** It comes from the code catalogue, resolved from
 * the key by the service: a category an operator could type would be a second
 * place the preference switch is decided, and the two would disagree the first
 * time somebody picked the wrong one from a dropdown.
 */
export const NotificationTemplateWritableShape = {
  isActive: z.boolean(),
  translations: NotificationTemplateTranslationsSchema,
};

/**
 * Creating a template (`notifications.manage`).
 *
 * `key` and `channel` are settable here and **nowhere else**: together they are
 * the row's identity (one template per key × channel), and moving either would
 * detach authored copy from the notice it was written for.
 *
 * The body ceiling depends on the channel (`bodyMaxLengthFor`), and the channel
 * is on this request — so the refinement runs here, field-scoped to the body it
 * concerns. The update DTO omits the channel; the service applies the same
 * ceiling against the row's own.
 */
export const NotificationTemplateCreateSchema = z
  .object({
    key: NotificationTemplateKeySchema,
    channel: NotificationChannelSchema,
    ...NotificationTemplateWritableShape,
  })
  .strict()
  .superRefine((request, ctx) => {
    const ceiling = bodyMaxLengthFor(request.channel);

    for (const locale of TRANSLATION_LOCALES) {
      if (request.translations[locale].body.length > ceiling) {
        ctx.addIssue({
          code: 'too_big',
          origin: 'string',
          maximum: ceiling,
          inclusive: true,
          path: ['translations', locale, 'body'],
          message: `A ${request.channel} body may be at most ${String(ceiling)} characters.`,
        });
      }
    }
  });

export type NotificationTemplateCreateRequest = z.infer<typeof NotificationTemplateCreateSchema>;
